import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, CheckCircle, AlertCircle, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

import {
  CandidateInfoSection,
  ProfessionalBackgroundSection,
  MilitaryBackgroundSection,
  EducationSection,
  JobSearchSection,
  CompensationScreeningSection,
} from './intake/IntakeSections';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL_FORM_DATA = {
  fullLegalName: '', preferredName: '', cityState: '', phoneNumber: '', email: '',
  howDidYouHear: '', currentEmploymentStatus: '', currentEmployer: '', currentJobTitle: '', datesOfEmployment: '',
  primaryResponsibilities: '', employeesSupervised: '', equipmentTechnologies: '', keyAccomplishments: '',
  branchOfService: '', yearsOfService: '', rankAtSeparation: '', militaryOccupation: '', militaryDuties: '',
  highestEducation: '', collegeUniversity: '', degreeMajor: '', tradeSchool: '', certifications: '',
  desiredJobTitles: '', preferredIndustries: '', preferredLocations: '', openToRelocation: '', willingToTravel: '',
  desiredSalaryRange: '', bonusExpectations: '', availabilityToStart: '', idealNextRole: '',
  authorizedToWork: '', requiresSponsorship: '', willingBackgroundCheck: '', willingDrugScreen: '', currentlyInterviewing: '',
  resumeFile: null,
};

const SECTION_REQUIRED_FIELDS = {
  1: [
    { field: 'fullLegalName', label: 'Full Legal Name' },
    { field: 'cityState', label: 'City / State' },
    { field: 'phoneNumber', label: 'Phone Number' },
    { field: 'email', label: 'Email Address' },
    { field: 'currentEmploymentStatus', label: 'Current Employment Status' },
  ],
  2: [],
  3: [],
  4: [],
  5: [
    { field: 'desiredJobTitles', label: 'Desired Job Titles' },
    { field: 'openToRelocation', label: 'Open to Relocation' },
  ],
  6: [
    { field: 'authorizedToWork', label: 'Authorized to Work in the US' },
  ],
};

const CandidateIntakeForm = ({ onBack }) => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [currentSection, setCurrentSection] = useState(1);
  const [validationErrors, setValidationErrors] = useState({});

  // Load Calendly widget script after successful submission
  useEffect(() => {
    if (!submitSuccess) return;
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      const existing = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existing) document.body.removeChild(existing);
    };
  }, [submitSuccess]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSelectChange = useCallback((name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files[0];
    setFormData(prev => ({ ...prev, resumeFile: file }));
  }, []);

  const validateSection = useCallback((section) => {
    const requiredFields = SECTION_REQUIRED_FIELDS[section] || [];
    const errors = {};
    let isValid = true;

    requiredFields.forEach(({ field, label }) => {
      if (!formData[field] || formData[field].trim() === '') {
        errors[field] = `${label} is required`;
        isValid = false;
      }
    });

    if (section === 1 && formData.email) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
        isValid = false;
      }
    }

    if (section === 1 && formData.phoneNumber) {
      if (!/^[\d\s\-\(\)\+]{10,}$/.test(formData.phoneNumber.replace(/\s/g, ''))) {
        errors.phoneNumber = 'Please enter a valid phone number';
        isValid = false;
      }
    }

    setValidationErrors(errors);
    return isValid;
  }, [formData]);

  const handleNextSection = () => {
    if (validateSection(currentSection)) {
      setCurrentSection(prev => prev + 1);
      setValidationErrors({});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateSection(6)) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key !== 'resumeFile' && formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });
      if (formData.resumeFile) {
        formDataToSend.append('resume', formData.resumeFile);
      }

      const response = await axios.post(`${API}/candidate/intake`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        setSubmitSuccess(true);
      }
    } catch (error) {
      let errorMessage = 'Failed to submit form. Please try again.';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => (typeof err === 'object' && err.msg) ? `${err.loc?.[1] || 'Field'}: ${err.msg}` : String(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object' && detail.msg) {
          errorMessage = detail.msg;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8 border-b-2 border-[#d4a574]">
          <CheckCircle className="text-green-500 mx-auto mb-4" size={64} />
          <h3 className="text-3xl font-bold text-[#0a1628] mb-3">Application Received!</h3>
          <p className="text-lg text-gray-700 mb-2">Thank you for submitting your candidate intake form.</p>
          <p className="text-gray-600">We've received your information and will review it carefully.</p>
        </div>

        <div className="bg-[#d4a574]/10 border-2 border-[#d4a574] rounded-lg p-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#d4a574] rounded-full mb-4">
            <Calendar className="text-[#0a1628]" size={32} />
          </div>
          <h4 className="text-2xl font-bold text-[#0a1628] mb-2">Next Step: Schedule Your Discovery Call</h4>
          <p className="text-lg text-gray-700 mb-1"><strong>Please schedule a call below to complete your application.</strong></p>
          <p className="text-gray-600 mb-4">Choose a 15-minute, 30-minute, or 1-hour time slot that works best for you.</p>
        </div>

        <div className="bg-white rounded-lg shadow-xl overflow-hidden border-2 border-[#d4a574]">
          <div className="calendly-inline-widget" data-url="https://calendly.com/rich-s2stalent?hide_gdpr_banner=1&primary_color=d4a574" style={{ minWidth: '320px', height: '700px' }}></div>
        </div>

        <div className="text-center pt-4">
          <p className="text-sm text-gray-600 mb-4">Available Monday-Friday, 8:00 AM - 5:00 PM Eastern Time</p>
          <Button onClick={onBack} variant="outline" className="border-[#0a1628] text-[#0a1628]">
            <ArrowLeft className="mr-2" size={18} />
            Return to Contact Page
          </Button>
        </div>
      </div>
    );
  }

  const sectionProps = { formData, validationErrors, onInputChange: handleInputChange, onSelectChange: handleSelectChange };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {submitError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start space-x-2">
          <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
          <span>{submitError}</span>
        </div>
      )}

      {/* Progress Indicator */}
      <div className="flex justify-between items-center mb-8">
        {[1, 2, 3, 4, 5, 6].map((section) => (
          <div key={section} className={`flex-1 h-2 mx-1 rounded-full ${section <= currentSection ? 'bg-[#d4a574]' : 'bg-gray-200'}`} />
        ))}
      </div>

      {currentSection === 1 && <CandidateInfoSection {...sectionProps} />}
      {currentSection === 2 && <ProfessionalBackgroundSection formData={formData} onInputChange={handleInputChange} />}
      {currentSection === 3 && <MilitaryBackgroundSection formData={formData} onInputChange={handleInputChange} onSelectChange={handleSelectChange} />}
      {currentSection === 4 && <EducationSection formData={formData} onInputChange={handleInputChange} onSelectChange={handleSelectChange} />}
      {currentSection === 5 && <JobSearchSection {...sectionProps} />}
      {currentSection === 6 && <CompensationScreeningSection {...sectionProps} onFileChange={handleFileChange} />}

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 border-t">
        {currentSection > 1 && (
          <Button type="button" variant="outline" onClick={() => { setCurrentSection(prev => prev - 1); setValidationErrors({}); }} className="border-[#0a1628] text-[#0a1628]" data-testid="btn-previous">
            Previous
          </Button>
        )}

        {currentSection === 1 && (
          <Button type="button" variant="outline" onClick={onBack} className="border-[#0a1628] text-[#0a1628]" data-testid="btn-back-to-contact">
            <ArrowLeft className="mr-2" size={18} />
            Back to Contact Options
          </Button>
        )}

        {currentSection < 6 ? (
          <Button type="button" onClick={handleNextSection} className="bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564] ml-auto" data-testid="btn-next">
            Next
          </Button>
        ) : (
          <Button type="submit" disabled={isSubmitting} className="bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564] ml-auto text-lg py-6 px-8" data-testid="btn-submit-application">
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </Button>
        )}
      </div>
    </form>
  );
};

export default CandidateIntakeForm;
